/**
 * PDF Embedder for XRechnung XML
 * Embeds XRechnung XML into PDF as per ZUGFeRD 2.x / Factur-X specification
 * Includes PDF/A-3 compliance additions (OutputIntent, color spaces)
 */

import {PDFDocument, PDFName, PDFDict, PDFArray, PDFHexString, PDFString, AFRelationship, PDFStream, PDFRef} from 'pdf-lib';

/**
 * ZUGFeRD/Factur-X profile information for XRechnung
 * Note: Version refers to Factur-X/ZUGFeRD version, NOT XRechnung spec version
 */
const ZUGFERD_PROFILE = {
    name: 'XRECHNUNG',
    // Factur-X 1.0 / ZUGFeRD 2.1 version (NOT XRechnung version)
    version: '1.0',
    // For XRechnung, use XRECHNUNG conformance level
    conformanceLevel: 'XRECHNUNG',
    // Document filename as per specification
    documentFileName: 'xrechnung.xml',
};

/**
 * Embed XRechnung XML into a PDF document
 * @param pdfBytes - Original PDF as ArrayBuffer or Uint8Array
 * @param xmlContent - XRechnung XML string
 * @param invoiceNumber - Invoice number for metadata
 * @returns Modified PDF with embedded XML
 */
export async function embedXmlInPdf(
    pdfBytes: ArrayBuffer | Uint8Array,
    xmlContent: string,
    invoiceNumber: string
): Promise<Uint8Array> {
    // Load the PDF document
    const pdfDoc = await PDFDocument.load(pdfBytes);

    // Convert XML string to bytes
    const xmlBytes = new TextEncoder().encode(xmlContent);

    // Embed the XML file
    // Using 'xrechnung.xml' as the filename for XRechnung profile
    // MIME type must be 'text/xml' for ZUGFeRD/Factur-X compliance
    // AFRelationship must be 'Alternative' (not 'Data') for hybrid documents
    await pdfDoc.attach(xmlBytes, ZUGFERD_PROFILE.documentFileName, {
        mimeType: 'text/xml',
        description: 'XRechnung invoice data (EN 16931 / UBL 2.1)',
        creationDate: new Date(),
        modificationDate: new Date(),
        afRelationship: AFRelationship.Alternative,
    });

    // Update document metadata
    pdfDoc.setTitle(`Rechnung ${invoiceNumber}`);
    pdfDoc.setSubject('XRechnung');
    pdfDoc.setKeywords(['XRechnung', 'ZUGFeRD', 'EN 16931', 'E-Invoice']);
    pdfDoc.setProducer('Bohringer Technik GmbH Invoice Generator');
    pdfDoc.setCreator('Bohringer Technik GmbH');

    // Add XMP metadata for ZUGFeRD compliance
    const xmpMetadata = generateZugferdXmp(invoiceNumber);

    // Embed XMP metadata into the PDF catalog
    try {
        embedXmpMetadata(pdfDoc, xmpMetadata);
    } catch (err) {
        console.warn('Could not embed XMP metadata:', err);
        // Continue without XMP - the embedded file is the critical part
    }

    // Add PDF/A-3 OutputIntent for color space compliance
    try {
        addOutputIntent(pdfDoc);
    } catch (err) {
        console.warn('Could not add OutputIntent:', err);
    }

    // Save and return the modified PDF
    return pdfDoc.save();
}

/**
 * Minimal sRGB ICC profile (IEC 61966-2.1)
 * This is a base64-encoded minimal sRGB profile for PDF/A compliance
 */
const SRGB_ICC_PROFILE_BASE64 = `
AAACHGxjbXMEMAAAbW50clJHQiBYWVogB+QACwAGAAoAGAAYYWNzcE1TRlQAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD21gABAAAAANMtbGNtcwAAAAAAAAAAAAAA
AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADmRlc2MAAAEAAAA0
Y3BydAAAAUQAAAAwcmRlc2MAAAFkAAAANHJYWVoAAAGYAAAAFGdYWVoAAAGsAAAA
FGJYWVoAAAHAAAAAFHd0cHQAAAHUAAAAFGNoYWQAAAHoAAAALHJUUkMAAAIUAAAA
IGdUUkMAAAI0AAAAIGJUUkMAAAJUAAAAIABzAFIARwBCACAAQgB1AGkAbAB0AC0A
aQBuAAAAAABQAEQAIABzAFIARwBCAAAAAABQAEQAIABzAFIARwBCAFhZWiAAAAFN
AAAAE7MAAAO+AAACbVhZWiAAAAAAAACJRQAAnpoAABlcWFlaIAAAAAAAAG0jAAC5
1gAAElNYWVogAAAAAAAA89sAAD2HAAB8i3NmMzIAAAAAAAEMQgAABd7///MLAAAL
TQAATHf//9cKAADg7QAA8ykAAQADAAAAAWFscGgAAAAFAAABcHJlYwAAAAIQAAAA
AQAAAA0AAAA0AAAAZG10cHgAAAAAAAA0AAAAZAAAYzMAQzIuMQAAAAAlawAAAGwA
AABrAAAAYG1sdWMAAAAAAAAAAQAAAAxlblVTAAAAEgAAABwAUABEACAAcwBSAEcA
Qg==`.replace(/\s/g, '');

/**
 * Add PDF/A-3 OutputIntent with sRGB ICC profile
 * This is required for PDF/A compliance to define color rendering intent
 */
function addOutputIntent(pdfDoc: PDFDocument): void {
    // Decode the ICC profile
    const iccBytes = Uint8Array.from(atob(SRGB_ICC_PROFILE_BASE64), c => c.charCodeAt(0));

    // Create ICC profile stream
    const iccStream = pdfDoc.context.stream(iccBytes, {
        N: 3, // Number of color components (RGB = 3)
    });
    const iccRef = pdfDoc.context.register(iccStream);

    // Create OutputIntent dictionary
    const outputIntentDict = pdfDoc.context.obj({
        Type: 'OutputIntent',
        S: 'GTS_PDFA1', // PDF/A output intent subtype
        OutputConditionIdentifier: 'sRGB IEC61966-2.1',
        RegistryName: 'http://www.color.org',
        Info: 'sRGB IEC61966-2.1',
        DestOutputProfile: iccRef,
    });
    const outputIntentRef = pdfDoc.context.register(outputIntentDict);

    // Create OutputIntents array and add to catalog
    const outputIntentsArray = pdfDoc.context.obj([outputIntentRef]);
    pdfDoc.catalog.set(PDFName.of('OutputIntents'), outputIntentsArray);
}

/**
 * Embed XMP metadata into PDF catalog
 * Creates a metadata stream in the document catalog
 */
function embedXmpMetadata(pdfDoc: PDFDocument, xmpContent: string): void {
    const xmpBytes = new TextEncoder().encode(xmpContent);

    // Create metadata stream
    const metadataStream = pdfDoc.context.stream(xmpBytes, {
        Type: 'Metadata',
        Subtype: 'XML',
    });

    // Register the stream
    const metadataRef = pdfDoc.context.register(metadataStream);

    // Add to catalog
    pdfDoc.catalog.set(PDFName.of('Metadata'), metadataRef);
}

/**
 * Generate ZUGFeRD/Factur-X XMP metadata with PDF/A-3 extension schema
 * This includes the required extension schema declaration for ZUGFeRD
 */
function generateZugferdXmp(invoiceNumber: string): string {
    const now = new Date().toISOString();

    // XMP packet with Factur-X/ZUGFeRD extension schema
    // The pdfaExtension schema is required for PDF/A-3 compliance
    return `<?xpacket begin="\uFEFF" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/">
  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
    <!-- PDF/A-3 extension schema for Factur-X/ZUGFeRD -->
    <rdf:Description rdf:about=""
        xmlns:pdfaExtension="http://www.aiim.org/pdfa/ns/extension/"
        xmlns:pdfaSchema="http://www.aiim.org/pdfa/ns/schema#"
        xmlns:pdfaProperty="http://www.aiim.org/pdfa/ns/property#">
      <pdfaExtension:schemas>
        <rdf:Bag>
          <rdf:li rdf:parseType="Resource">
            <pdfaSchema:schema>Factur-X PDFA Extension Schema</pdfaSchema:schema>
            <pdfaSchema:namespaceURI>urn:factur-x:pdfa:CrossIndustryDocument:invoice:1p0#</pdfaSchema:namespaceURI>
            <pdfaSchema:prefix>fx</pdfaSchema:prefix>
            <pdfaSchema:property>
              <rdf:Seq>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>DocumentFileName</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>Name of the embedded XML invoice file</pdfaProperty:description>
                </rdf:li>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>DocumentType</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>INVOICE</pdfaProperty:description>
                </rdf:li>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>Version</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>The actual Factur-X version</pdfaProperty:description>
                </rdf:li>
                <rdf:li rdf:parseType="Resource">
                  <pdfaProperty:name>ConformanceLevel</pdfaProperty:name>
                  <pdfaProperty:valueType>Text</pdfaProperty:valueType>
                  <pdfaProperty:category>external</pdfaProperty:category>
                  <pdfaProperty:description>The conformance level of the embedded invoice</pdfaProperty:description>
                </rdf:li>
              </rdf:Seq>
            </pdfaSchema:property>
          </rdf:li>
        </rdf:Bag>
      </pdfaExtension:schemas>
    </rdf:Description>
    <!-- Factur-X/ZUGFeRD invoice properties -->
    <rdf:Description rdf:about=""
        xmlns:fx="urn:factur-x:pdfa:CrossIndustryDocument:invoice:1p0#">
      <fx:DocumentFileName>${ZUGFERD_PROFILE.documentFileName}</fx:DocumentFileName>
      <fx:DocumentType>INVOICE</fx:DocumentType>
      <fx:Version>${ZUGFERD_PROFILE.version}</fx:Version>
      <fx:ConformanceLevel>${ZUGFERD_PROFILE.conformanceLevel}</fx:ConformanceLevel>
    </rdf:Description>
    <!-- PDF/A identification -->
    <rdf:Description rdf:about=""
        xmlns:pdfaid="http://www.aiim.org/pdfa/ns/id/">
      <pdfaid:part>3</pdfaid:part>
      <pdfaid:conformance>B</pdfaid:conformance>
    </rdf:Description>
    <!-- Dublin Core metadata -->
    <rdf:Description rdf:about=""
        xmlns:dc="http://purl.org/dc/elements/1.1/">
      <dc:title>
        <rdf:Alt>
          <rdf:li xml:lang="x-default">Rechnung ${invoiceNumber}</rdf:li>
        </rdf:Alt>
      </dc:title>
      <dc:creator>
        <rdf:Seq>
          <rdf:li>Bohringer Technik GmbH</rdf:li>
        </rdf:Seq>
      </dc:creator>
      <dc:description>
        <rdf:Alt>
          <rdf:li xml:lang="x-default">XRechnung elektronische Rechnung nach EN 16931</rdf:li>
        </rdf:Alt>
      </dc:description>
      <dc:format>application/pdf</dc:format>
    </rdf:Description>
    <!-- XMP basic metadata -->
    <rdf:Description rdf:about=""
        xmlns:xmp="http://ns.adobe.com/xap/1.0/">
      <xmp:CreateDate>${now}</xmp:CreateDate>
      <xmp:ModifyDate>${now}</xmp:ModifyDate>
      <xmp:MetadataDate>${now}</xmp:MetadataDate>
      <xmp:CreatorTool>Bohringer Technik GmbH Invoice Generator</xmp:CreatorTool>
    </rdf:Description>
    <!-- PDF producer/creator -->
    <rdf:Description rdf:about=""
        xmlns:pdf="http://ns.adobe.com/pdf/1.3/">
      <pdf:Producer>Bohringer Technik GmbH Invoice Generator with pdf-lib</pdf:Producer>
    </rdf:Description>
  </rdf:RDF>
</x:xmpmeta>
<?xpacket end="w"?>`;
}

/**
 * Extract embedded XML from a ZUGFeRD/Factur-X PDF
 * @param pdfBytes - PDF file bytes
 * @returns XML content if found, null otherwise
 */
export async function extractXmlFromPdf(pdfBytes: ArrayBuffer | Uint8Array): Promise<string | null> {
    try {
        const pdfDoc = await PDFDocument.load(pdfBytes);

        // Get the names dictionary to find embedded files
        const catalog = pdfDoc.catalog;
        const namesDict = catalog.lookup(PDFName.of('Names'));

        if (!namesDict) {
            return null;
        }

        // Look for embedded files named 'xrechnung.xml', 'factur-x.xml', or 'zugferd-invoice.xml'
        // This is a simplified implementation - full extraction would need
        // to traverse the EmbeddedFiles name tree

        return null; // Placeholder - would need more complex logic
    } catch (error) {
        console.error('Error extracting XML from PDF:', error);
        return null;
    }
}

/**
 * Check if a PDF contains ZUGFeRD/XRechnung data
 * @param pdfBytes - PDF file bytes
 * @returns True if ZUGFeRD data is found
 */
export async function hasEmbeddedXml(pdfBytes: ArrayBuffer | Uint8Array): Promise<boolean> {
    try {
        const pdfDoc = await PDFDocument.load(pdfBytes);

        // Check for embedded files
        const catalog = pdfDoc.catalog;
        const namesDict = catalog.lookup(PDFName.of('Names'));

        return namesDict !== undefined;
    } catch {
        return false;
    }
}
